import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import InputField from '@/components/atoms/InputField';
import euroIcon from '@/assets/icons/euro.svg';

// Import the form schema from types
import { createRevenueCategoryFormSchema, RevenueCategoryFormValues } from '@/services/types/revenue-category.types';
import TextAreaField from '@/components/atoms/TextAreaField';
import SliderInputField from '@/components/molecules/SliderInputField';
import SliderField from '@/components/atoms/SliderField';
import InfoModal from '@/components/molecules/InfoModal';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import profileDiscardIcon from '@/assets/icons/profile-discard.svg';

interface RCCreationFormProps {
  onSubmit?: (data: RevenueCategoryFormValues) => void | Promise<void>;
  className?: string;
  isSubmitting?: boolean;
  defaultValues?: Partial<RevenueCategoryFormValues>;
  isEditMode?: boolean;
  maxRevenueValue?: number;
  onFormStateChange?: (state: { isDirty: boolean }) => void;
  onCancel?: () => void;
}

const RCCreationForm: React.FC<RCCreationFormProps> = ({
  onSubmit,
  className,
  isSubmitting = false,
  defaultValues,
  isEditMode = false,
  maxRevenueValue,
  onFormStateChange,
  onCancel
}) => {
  const navigate = useNavigate();
  const [rcrtError, setRcrtError] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const formSchema = useMemo(() => createRevenueCategoryFormSchema(maxRevenueValue), [maxRevenueValue]);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty }
  } = useForm<RevenueCategoryFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues || {
      name: '',
      description: '',
      rcrt: 0,
      rcrt_alert_threshold: 0
    }
  });

  // Reset form when defaultValues change
  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  useEffect(() => {
    onFormStateChange?.({ isDirty });
  }, [isDirty, onFormStateChange]);

  const rcrt = watch('rcrt');
  const rcrt_alert_threshold = watch('rcrt_alert_threshold');
  const isBusy = isSubmitting || isFormSubmitting;

  // Debug: log errors if any
  if (Object.keys(errors).length > 0) {
    console.log('Form errors:', errors);
  }

  const onSubmitForm = async (data: RevenueCategoryFormValues) => {
    if (onSubmit) {
      setIsFormSubmitting(true);
      try {
        await Promise.resolve(
          onSubmit({
            ...data,
            rcrt: Number(data.rcrt),
            rcrt_alert_threshold: Number(data.rcrt_alert_threshold)
          })
        );
      } finally {
        setIsFormSubmitting(false);
      }
    }
  };

  const onInvalidForm = () => {
    toast.error('Please correct the invalid form fields');
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    if (isDirty) {
      setIsDiscardModalOpen(true);
      return;
    }
    navigate(-1);
  };

  const handleDiscardChanges = () => {
    setIsDiscardModalOpen(false);
    navigate(-1);
  };

  const handleRcrtExceed = () => {
    toast.error(`RCRT cannot exceed BTRT (€${maxRevenueValue?.toLocaleString()})`);
    setRcrtError(true);
    setTimeout(() => setRcrtError(false), 3000);
  };

  return (
    <>
      <form className={`flex-1 flex flex-col gap-4 ${className}`} onSubmit={handleSubmit(onSubmitForm, onInvalidForm)}>
        <div className="space-y-5 flex-1">
          <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
            <InputField
              className="flex-1 w-full"
              label="Revenue category name"
              placeholder="Enter revenue category name..."
              name="name"
              type="text"
              register={register}
              error={errors.name}
              disabled={isBusy}
              size="sm"
            />
            <span className="hidden md:block flex-1"></span>
          </div>

          <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
            <TextAreaField
              name="description"
              label="Revenue category Description"
              placeholder="Enter revenue category description..."
              register={register}
              className="w-full"
              error={errors.description}
              labelClassName="md:text-sm"
              disabled={isBusy}
            />
          </div>

          <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
            <div className="flex-1 w-full">
              <SliderInputField
                name="rcrt"
                label="Revenue category max revenue target (RCRT)"
                inputPlaceholder="e.g 100000"
                value={Number(rcrt) || 0}
                onChange={(val) => setValue('rcrt', val as number, { shouldDirty: true })}
                error={errors.rcrt || rcrtError}
                min={0}
                maxValue={maxRevenueValue || 100000}
                icon={euroIcon}
                tooltipText={`RCRT cannot exceed BTRT (€${maxRevenueValue?.toLocaleString()})`}
                onValueExceed={handleRcrtExceed}
              />

              <p className={`text-sm mt-[0.35rem] ${rcrtError ? 'text-red-500' : 'text-secondary'}`}>
                RCRT cannot exceed BTRT (€{maxRevenueValue?.toLocaleString()})
              </p>
            </div>
            <SliderField
              name="rcrt_alert_threshold"
              label="Alerting threshold of RCRT"
              value={[Number(rcrt_alert_threshold) || 0]}
              onValueChange={([value]) => setValue('rcrt_alert_threshold', value, { shouldDirty: true })}
              className="flex-1 w-full"
              error={errors.rcrt_alert_threshold}
            />
          </div>

          <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
            <span className="hidden md:block flex-1"></span>
          </div>
        </div>
        {/* FOOTER */}
        <footer className="py-2 bg-gradient-to-b from-[#F0EFFD]/0 from-0% to-[#F0EFFD] to-[17.74%] md:bg-white md:bg-none md:from-transparent md:to-transparent md:flex md:justify-end">
          <Button
            className="hidden md:block rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit text-[#E8507E]"
            variant={'text'}
            onClick={handleCancel}
            type="button"
          >
            Cancel
          </Button>
          <Button
            className="rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit"
            type="submit"
            disabled={!isDirty || isBusy}
          >
            {isBusy ? (isEditMode ? 'SAVING...' : 'CREATING...') : isEditMode ? 'SAVE' : 'CREATE'}
          </Button>
        </footer>
      </form>

      {!onCancel && isDiscardModalOpen && (
        <InfoModal
          isOpen={isDiscardModalOpen}
          variant="confirm"
          title="Are you sure you want to discard changes?"
          message="Unsaved changes will not be saved into your revenue category"
          buttonText="Discard"
          onButtonClick={handleDiscardChanges}
          icon={profileDiscardIcon}
          onClose={() => setIsDiscardModalOpen(false)}
        />
      )}
    </>
  );
};

export default RCCreationForm;
